import Grid from '@mui/material/Grid';
import { COUNTRY_WITH_PROVINCES, errorMessage } from 'constants/CommonConstants';
import { formRequiredRules } from 'helper';
import React, { useImperativeHandle } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AutoCompleteAddress } from 'sharedComponents/inputGroup/AutoCompleteAddress';
import { Lead } from '../LeadGenInterface';
import { CommonInput, SelectInput } from 'sharedComponents/inputGroup';
import { InputRadio } from 'sharedComponents/inputGroup/InputRadio';
import { ADDRESS_OPTION } from '../LeadGenConstant';
import { postLeadCreation } from 'store/LeadCreationState';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import { useDispatch, useSelector } from 'react-redux';
import { StepperFromProps } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';

const Address: React.ForwardRefRenderFunction<unknown, StepperFromProps> = (
  { onNext, onBack, isSubmit, mappedData },
  ref
) => {
  // Hooks
  const { t } = useTranslation();

  const hasCalledNextStep = React.useRef(false);

  // Redux
  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();

  const { authenticateClientData } = useSelector((state: RootState) => state.auth) || {};
  const widgetData = authenticateClientData?.data?.widgetData || {};

  // React Hook Form
  const { control, trigger, getValues, watch, setValue } = useFormContext();

  // Fields to reset  when address option change
  const fieldsToReset = [
    'address',
    'city',
    'streetAddress',
    'postalCode',
    'province',
    'manualStreetAddress',
    'manualStreetNumber',
  ];

  // React Hook Form Watcher
  const addressWatcher = watch('addressOption');
  const countryWatcher = watch('country');
  const provinceWatcher = watch('province');

  // React Hook Form Trigger
  const nextStep = async () => {
    if (hasCalledNextStep.current) return;
    hasCalledNextStep.current = true;

    const {
      city,
      country,
      postalCode,
      province,
      streetAddress,
      manualStreetNumber,
      manualStreetAddress,
      streetAddress_province,
      streetAddress_postalCode,
      streetAddress_country,
      streetAddress_code,
      streetAddress_selectedAddress,
      streetAddress_city,
      streetAddress_postal,
      streetAddress_lookupStreetName,
      streetAddress_lookupStreetNumber,
    } = getValues();

    const commonAddressData: Partial<Lead['leadDetails']> = {
      City: city || streetAddress_city,
      'Postal Code': postalCode || streetAddress_postalCode,
      Province: province || streetAddress_province,
      'Street Address': addressWatcher === 'manual' ? manualStreetAddress : streetAddress?.label,
      ...mappedData,
    };

    let addressData: Lead;

    if (addressWatcher === 'manual') {
      addressData = {
        province: province,
        zipCode: widgetData?.zipCode || postalCode,
        originalZipCode: postalCode,
        country: country,
        leadSourceId: widgetData?.leadSourceId,
        isSubmit,
        leadDetails: commonAddressData,
        city: city,
        streetName: manualStreetAddress,
        addressNumber: manualStreetNumber,
      };
    } else {
      addressData = {
        isSubmit,
        province: streetAddress_province,
        zipCode: widgetData?.zipCode || streetAddress_postal,
        originalZipCode: streetAddress_postal,
        country: streetAddress_country,
        leadSourceId: widgetData?.leadSourceId,
        streetName: streetAddress_lookupStreetName,
        addressNumber: streetAddress_lookupStreetNumber,
        city: streetAddress_city,
        leadDetails: {
          ...commonAddressData,
          Code: streetAddress_code,
          Country: streetAddress_country,
          'Selected Address': streetAddress_selectedAddress,
        },
      };
    }

    dispatch(postLeadCreation({ data: addressData, leadTypeID: widgetData?.leadTypeId })).then((res) => {
      if (
        res?.payload?.success &&
        (res?.payload?.data?.draftLeadId || res?.payload?.data?._id || res?.payload?.data?.Lead?.leadId)
      ) {
        onNext?.();
      }
    });
  };

  const backStep = () => {
    const { employmentStatus, workingWhileInSchool } = getValues();

    console.log('Address Step - stepConfig', widgetData?.stepConfig?.includes('EmploymentStatusStep'));
    console.log('Address Step - widgetData?.stepConfig', widgetData?.stepConfig);

    const employmentStatusStepIndex = widgetData?.stepConfig?.indexOf('EmploymentStatusStep');
    console.log('EmploymentStatusStep index:', employmentStatusStepIndex);
    const yearsOfIncomeStepIndex = widgetData?.stepConfig?.indexOf('YearsOfIncomeStep');

    employmentStatus === 'Employed'
      ? onBack?.()
      : employmentStatus === 'Student' && workingWhileInSchool === 'no'
        ? employmentStatusStepIndex > -1
          ? onBack?.(employmentStatusStepIndex) // 4 EmploymentStatusStep
          : onBack?.()
        : yearsOfIncomeStepIndex > -1
          ? onBack?.(yearsOfIncomeStepIndex) // 7 YearsOfIncomeStep
          : onBack?.();
  };

  useImperativeHandle(ref, () => ({
    nextStep,
    backStep,
  }));

  // Reset fields when address option change
  const handleReset = () => {
    fieldsToReset.forEach((field) => setValue(field, ''));
    // fieldsToReset.forEach((field) => resetField(field));
  };

  const COUNTRY_OPTIONS = COUNTRY_WITH_PROVINCES.map(({ label, value }) => ({ label, value }));
  const selectedCountry = COUNTRY_WITH_PROVINCES.find((c) => c.value === countryWatcher);
  const regionOptions = selectedCountry ? selectedCountry.provinces : [];

  return (
    <>
      <h3 className='dy-stepper-form__body__title text-center pm'>{t('leadGen.addressTxt')}</h3>
      <Grid
        container
        rowSpacing={3}
        columnSpacing={2}
        className='h-100'
        sx={{ pb: '1px', overflowY: 'auto', overflowX: 'hidden' }}
      >
        <Grid item xs={12}>
          <InputRadio
            rules={formRequiredRules(errorMessage(t).radioError)}
            name={'addressOption'}
            control={control}
            options={ADDRESS_OPTION(t)}
            directionRow={false}
            itemPerRow={{
              xs: 12,
              sm: 6,
            }}
            radioVariant='outline'
            triggerValidationForField={(name: string) => {
              trigger(name);
              handleReset();
            }}
          />
        </Grid>

        {addressWatcher === 'manual' ? (
          <>
            {/* manual enter address */}
            <Grid item sm={6} xs={12}>
              <CommonInput
                name='manualStreetAddress'
                control={control}
                label={t('leadGen.streetAddress')}
                placeholder={t('stepperForm.enterAddress')}
                triggerValidationForField={trigger}
                rules={formRequiredRules(errorMessage(t).addressRequired)}
              />
            </Grid>

            <Grid item sm={6} xs={12}>
              <CommonInput
                name='manualStreetNumber'
                control={control}
                label={t('leadGen.streetNumber')}
                placeholder={t('stepperForm.enterStreetNumber')}
                triggerValidationForField={trigger}
                rules={formRequiredRules(errorMessage(t).required)}
              />
            </Grid>

            <Grid item sm={6} xs={12}>
              <CommonInput
                name='city'
                control={control}
                label={t('leadGen.cityorTown')}
                placeholder={t('leadGen.enterCityorTown')}
                triggerValidationForField={trigger}
                rules={formRequiredRules(errorMessage(t).cityOrTownRequired)}
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <CommonInput
                name='postalCode'
                control={control}
                label={t('stepperForm.postalCode')}
                placeholder={t('stepperForm.enterPostalCode')}
                triggerValidationForField={trigger}
                rules={formRequiredRules(errorMessage(t).postalCodeRequired)}
              />
            </Grid>

            <Grid item sm={6} xs={12}>
              <SelectInput
                name='country'
                control={control}
                label={t('stepperForm.country')}
                rules={formRequiredRules(errorMessage(t).required)}
                options={COUNTRY_OPTIONS}
                triggerValidationForField={trigger}
                defaultValue={countryWatcher}
              />
            </Grid>

            {countryWatcher && (
              <Grid item sm={6} xs={12}>
                <SelectInput
                  name='province'
                  control={control}
                  label={t('stepperForm.province')}
                  placeholder={t('leadGen.enterProvince')}
                  rules={formRequiredRules(errorMessage(t).required)}
                  options={regionOptions}
                  triggerValidationForField={trigger}
                  defaultValue={provinceWatcher}
                />
              </Grid>
            )}

            {/* <Grid item sm={6} xs={12}>
              <CommonInput
                name='province'
                control={control}
                label={t('stepperForm.province')}
                placeholder={t('leadGen.enterProvince')}
                triggerValidationForField={trigger}
                rules={formRequiredRules(errorMessage(t).provinceRequired)}
              />
            </Grid> */}
            {/* manual enter address */}
          </>
        ) : (
          <Grid item xs={12}>
            <AutoCompleteAddress
              name='streetAddress'
              control={control}
              setValue={setValue}
              label={t('stepperForm.address')}
              placeholder={t('stepperForm.enterAddress')}
              rules={formRequiredRules(errorMessage(t).addressRequired)}
              triggerValidationForField={trigger}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};

const AddressStep = React.forwardRef(Address);

export default AddressStep;
